import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';

// A small router: each section and shared page has its own address, and Back returns
// to the exact spot (the scroll position is kept with each history entry).
// History state may say where a shared page was opened from ("from"), so the book page
// can say "← Back to Shelves" and, on a laptop, open as a side panel over that section.

export interface RouteState {
  /** The address a shared page was opened from (e.g. "/shelves"). */
  from?: string;
  /** Scroll position to return to, saved when leaving this entry. */
  scrollY?: number;
  /** Show a book as the full page even on a laptop ("Open full page"). */
  full?: boolean;
}

interface Route {
  path: string;
  state: RouteState;
}

const listeners = new Set<(r: Route) => void>();

function current(): Route {
  return { path: window.location.pathname || '/', state: (history.state as RouteState | null) ?? {} };
}

function saveScroll() {
  const st = (history.state as RouteState | null) ?? {};
  history.replaceState({ ...st, scrollY: window.scrollY }, '');
}

function emit() {
  const r = current();
  for (const l of listeners) l(r);
}

/** Go to an address. The scroll position of the page you leave is kept for Back. */
export function navigate(path: string, opts: { replace?: boolean; state?: RouteState; keepScroll?: boolean } = {}) {
  saveScroll();
  const state = opts.state ?? {};
  if (opts.replace) history.replaceState(state, '', path);
  else history.pushState(state, '', path);
  emit();
  if (!opts.keepScroll) window.scrollTo(0, 0);
}

/** Back one step if this page was opened from inside the app; otherwise go to `fallback`. */
export function goBack(fallback: string) {
  const st = (history.state as RouteState | null) ?? {};
  if (st.from) history.back();
  else navigate(fallback, { replace: true });
}

if (typeof window !== 'undefined') {
  history.scrollRestoration = 'manual';
  window.addEventListener('popstate', () => {
    emit();
    const y = ((history.state as RouteState | null) ?? {}).scrollY ?? 0;
    // After the page has drawn at its new address.
    requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, y)));
  });
}

export function useRoute(): Route {
  const [r, setR] = useState<Route>(current);
  useEffect(() => {
    listeners.add(setR);
    return () => void listeners.delete(setR);
  }, []);
  return r;
}

/** A link inside the app: changes the address without reloading the page. */
export function Link(props: {
  href: string;
  state?: RouteState;
  class?: string;
  children: ComponentChildren;
  'aria-current'?: 'page' | undefined;
  onSame?: () => void;
  /** Keep the page where it is (a laptop's side panel opens over it). */
  keepScroll?: boolean;
}) {
  const { href, state, onSame, keepScroll, children, ...rest } = props;
  return (
    <a
      href={href}
      {...rest}
      onClick={(e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        if (window.location.pathname === href && onSame) onSame();
        else navigate(href, { state, keepScroll });
      }}
    >
      {children}
    </a>
  );
}
